<template>
  <div
    class="alex-chat"
    :style="chatThemeVars"
    :class="{ 'alex-chat--embedded': embedded, 'alex-chat--dragover': isDragOver }"
    @dragenter.prevent="onChatDragEnter"
    @dragover.prevent="onChatDragOver"
    @dragleave.prevent="onChatDragLeave"
    @drop.prevent="onChatDrop"
  >
    <div v-if="isDragOver" class="alex-drop-overlay" aria-live="polite">
      <i class="fa fa-paperclip" aria-hidden="true"></i>
      <span>Drop file anywhere to attach</span>
    </div>
    <div v-if="showSessionControls" class="alex-chat__toolbar">
      <button type="button" class="alex-btn alex-btn--primary" :disabled="isBootstrapping || isSending" @click="createSession">New chat</button>
      <div ref="sessionDropdownRoot" class="alex-toolbar-label alex-session-panel">
        <button
          type="button"
          class="alex-session-trigger"
          :disabled="isBootstrapping || isSending"
          :aria-expanded="sessionDropdownOpen ? 'true' : 'false'"
          aria-haspopup="listbox"
          aria-label="Open Chat History"
          @click.stop="toggleSessionDropdown"
        >
          <span class="alex-session-trigger__text">Open Chat History</span>
          <i
            class="fa fa-chevron-down alex-session-trigger__caret"
            :class="{ 'alex-session-trigger__caret--open': sessionDropdownOpen }"
            aria-hidden="true"
          ></i>
        </button>
        <div
          v-show="sessionDropdownOpen"
          class="alex-session-dropdown"
          role="listbox"
          :aria-label="'Chat sessions'"
          @click.stop
        >
          <div class="alex-session-list" role="presentation">
            <div
              v-for="s in sessions"
              :key="s.in_chat_session_id"
              class="alex-session-row"
              :class="{ 'alex-session-row--active': selectedSessionId === s.in_chat_session_id }"
              role="option"
              :aria-selected="selectedSessionId === s.in_chat_session_id ? 'true' : 'false'"
            >
              <template v-if="editingSessionId === s.in_chat_session_id">
                <input
                  v-model="editTitleDraft"
                  class="alex-session-input"
                  type="text"
                  maxlength="120"
                  :aria-label="'Rename session ' + s.in_chat_session_id"
                  @keydown.enter.prevent="saveSessionRename(s)"
                  @keydown.esc.prevent="cancelSessionRename"
                />
                <button
                  type="button"
                  class="alex-session-icon alex-session-icon--save"
                  title="Save"
                  aria-label="Save session name"
                  @click="saveSessionRename(s)"
                >
                  <i class="fa fa-check" aria-hidden="true"></i>
                </button>
                <button
                  type="button"
                  class="alex-session-icon"
                  title="Cancel"
                  aria-label="Cancel rename"
                  @click="cancelSessionRename"
                >
                  <i class="fa fa-times" aria-hidden="true"></i>
                </button>
              </template>
              <template v-else>
                <button
                  type="button"
                  class="alex-session-select"
                  :class="{ 'alex-session-select--active': selectedSessionId === s.in_chat_session_id }"
                  @click="pickSession(s)"
                >
                  {{ s.st_session_title }}
                </button>
                <button
                  type="button"
                  class="alex-session-pencil"
                  title="Edit session name"
                  aria-label="Edit session name"
                  @click.stop="startSessionRename(s)"
                >
                  <i class="fa fa-pencil" aria-hidden="true"></i>
                </button>
                <button
                  type="button"
                  class="alex-session-delete"
                  title="Delete session"
                  aria-label="Delete session"
                  @click.stop="deleteSession(s)"
                >
                  <i class="fa fa-trash-o" aria-hidden="true"></i>
                </button>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div ref="stream" class="alex-chat__stream">
      <p v-if="!selectedSessionId" class="alex-chat__hint">
        {{ showSessionControls
          ? "Start a New chat or pick a session. Messages, uploads, and generated JSON all appear here."
          : "Loading Tracey chat session..." }}
      </p>

      <div
        v-for="msg in displayMessages"
        :key="msg.in_chat_message_id"
        class="alex-bubble"
        :class="bubbleClasses(msg)"
      >
        <div class="alex-bubble__role">{{ labelRole(msg.st_role) }}</div>
        <div v-if="!isSending && messageFormPreview(msg.st_message)" class="alex-preview alex-preview--inline">
          <button
            type="button"
            class="alex-preview__toggle"
            @click="toggleMessagePreview(msg)"
          >
            <h4 class="alex-preview__title">
              {{ messageFormPreview(msg.st_message).form.title || "Generated Form" }}
            </h4>
            <p class="alex-preview__meta">
              {{ (messageFormPreview(msg.st_message).questions || []).length }} questions generated
              <span
                v-if="Array.isArray(messageFormPreview(msg.st_message).skip_logic) && messageFormPreview(msg.st_message).skip_logic.length > 0"
              >
                &middot; {{ messageFormPreview(msg.st_message).skip_logic.length }} skip logic rule{{ messageFormPreview(msg.st_message).skip_logic.length > 1 ? 's' : '' }}
              </span>
            </p>
            <span class="alex-preview__toggle-label">
              {{ isMessagePreviewExpanded(msg) ? "Hide details" : "Show details" }}
            </span>
          </button>
          <div
            v-if="isMessagePreviewExpanded(msg)"
            class="alex-preview__expanded"
          >
            <div
              v-for="(q, idx) in (messageFormPreview(msg.st_message).questions || [])"
              :key="'msg-q-' + msg.in_chat_message_id + '-' + idx"
              class="alex-preview__question"
            >
              <div class="alex-preview__qtitle">
                {{ idx + 1 }}. {{ questionTitle(q) }}
                <span v-if="isQuestionRequired(q)" class="alex-preview__required">Required</span>
              </div>
              <div class="alex-preview__qmeta">
                Type: {{ questionType(q) }}
                <span v-if="!isQuestionRequired(q)" class="alex-preview__optional">Optional</span>
              </div>
            </div>
            <div
              v-if="Array.isArray(messageFormPreview(msg.st_message).skip_logic) && messageFormPreview(msg.st_message).skip_logic.length"
              class="alex-preview__skiplogic"
            >
              <div class="alex-preview__skiplogic-title">
                <i class="fa fa-random" aria-hidden="true"></i> Skip Logic
              </div>
              <div
                v-for="(sl, slIdx) in messageFormPreview(msg.st_message).skip_logic"
                :key="'msg-sl-' + msg.in_chat_message_id + '-' + slIdx"
                class="alex-preview__skiplogic-rule"
              >
                <span class="alex-preview__sl-label">If</span>
                Q{{ (sl.source_question_index || 0) + 1 }}
                <template
                  v-if="sl.parent_question_index != null && sl.parent_question_index !== sl.source_question_index"
                >
                  <span class="alex-preview__sl-parent">
                    (when leaving Q{{ sl.parent_question_index + 1 }})
                  </span>
                </template>
                is {{ sl.condition || 'ANSWERED' }}
                <template v-if="sl.answer_values && sl.answer_values.length">
                  ({{ sl.match_type || 'EQUALS' }} {{ sl.inclusion || 'ALL' }}: {{ sl.answer_values.join(', ') }})
                </template>
                <span class="alex-preview__sl-label">then</span>
                <template
                  v-if="sl.then_question_index === -99 || sl.then_question_index === -2"
                >End</template>
                <template v-else>go to Q{{ (sl.then_question_index || 0) + 1 }}</template>
                <span class="alex-preview__sl-label">else</span>
                <template
                  v-if="sl.else_question_index === -99 || sl.else_question_index === -2"
                >End</template>
                <template v-else>go to Q{{ (sl.else_question_index || 0) + 1 }}</template>
              </div>
            </div>
          </div>
        </div>
        <pre v-else class="alex-bubble__text">{{ formatDisplayMessage(msg) }}</pre>
      </div>


      <div v-if="showGeneratedPreview" class="alex-bubble alex-bubble--json">
        <div class="alex-bubble__role">Generated form preview</div>

        <div v-if="generatedParsed" class="alex-preview">
          <h4 class="alex-preview__title">{{ generatedFormTitle }}</h4>
          <p class="alex-preview__meta">
            {{ generatedQuestionCount }} questions generated
            <span v-if="generatedSkipLogicCount > 0">
              &middot; {{ generatedSkipLogicCount }} skip logic rule{{ generatedSkipLogicCount > 1 ? 's' : '' }}
            </span>
          </p>

          <div v-if="generatedQuestions.length" class="alex-preview__questions">
            <div
              v-for="(q, idx) in generatedQuestions"
              :key="idx"
              class="alex-preview__question"
            >
              <div class="alex-preview__qtitle">
                {{ idx + 1 }}. {{ questionTitle(q) }}
                <span v-if="isQuestionRequired(q)" class="alex-preview__required">Required</span>
              </div>
              <div class="alex-preview__qmeta">
                Type: {{ questionType(q) }}
                <span v-if="!isQuestionRequired(q)" class="alex-preview__optional">Optional</span>
              </div>
            </div>
          </div>

          <div v-if="generatedSkipLogic.length" class="alex-preview__skiplogic">
            <div class="alex-preview__skiplogic-title">
              <i class="fa fa-random" aria-hidden="true"></i> Skip Logic
            </div>
            <div
              v-for="(sl, slIdx) in generatedSkipLogic"
              :key="'sl-' + slIdx"
              class="alex-preview__skiplogic-rule"
            >
              <span class="alex-preview__sl-label">If</span>
              Q{{ (sl.source_question_index || 0) + 1 }}
              <template
                v-if="sl.parent_question_index != null && sl.parent_question_index !== sl.source_question_index"
              >
                <span class="alex-preview__sl-parent">
                  (when leaving Q{{ sl.parent_question_index + 1 }})
                </span>
              </template>
              is {{ sl.condition || 'ANSWERED' }}
              <template v-if="sl.answer_values && sl.answer_values.length">
                ({{ sl.match_type || 'EQUALS' }} {{ sl.inclusion || 'ALL' }}: {{ sl.answer_values.join(', ') }})
              </template>
              <span class="alex-preview__sl-label">then</span>
              <template
                v-if="sl.then_question_index === -99 || sl.then_question_index === -2"
              >End</template>
              <template v-else>go to Q{{ (sl.then_question_index || 0) + 1 }}</template>
              <span class="alex-preview__sl-label">else</span>
              <template
                v-if="sl.else_question_index === -99 || sl.else_question_index === -2"
              >End</template>
              <template v-else>go to Q{{ (sl.else_question_index || 0) + 1 }}</template>
            </div>
          </div>
        </div>

        <div v-else>
          <p class="alex-json-hint">Generated response is not valid form JSON yet.</p>
        </div>

        <div class="alex-json-details">
          <button type="button" class="alex-json-toggle" @click="showRawJson = !showRawJson">
            {{ showRawJson ? "Hide raw JSON" : "Show raw JSON" }}
          </button>
        </div>
        <div v-if="showRawJson">
          <textarea
            v-model="generatedRaw"
            class="alex-json-editor"
            rows="10"
            spellcheck="false"
          ></textarea>
        </div>

        <button
          type="button"
          class="alex-btn alex-btn--apply"
          :disabled="isApplying || isSending || isBootstrapping"
          @click="applyGenerated"
        >
          <span v-if="isApplying" class="alex-btn__loader" aria-hidden="true"></span>
          {{ applyButtonLabel }}
        </button>
        <p v-if="hasAppliedFormMappings" class="alex-apply-hint">
          Tracey will update existing applied fields in place. New fields in the preview are added to the form.
        </p>
      </div>

      <div
        v-if="isSending"
        ref="sendProgress"
        class="alex-progress alex-progress--stream-end"
        role="status"
        aria-live="polite"
        :aria-label="sendPhaseLabel"
      >
        <div class="alex-progress__header">
          <img
            class="alex-progress__avatar"
            :src="traceyProgressImg"
            alt=""
            width="40"
            height="40"
          />
          <div>
            <div class="alex-progress__title">Tracey is working on your form</div>
            <p class="alex-progress__subtitle">This may take a moment…</p>
          </div>
        </div>
        <ol class="alex-progress__steps">
          <li
            v-for="(step, idx) in sendProgressSteps"
            :key="step.id"
            class="alex-progress__step"
            :class="{
              'alex-progress__step--done': idx < sendPhaseIndex,
              'alex-progress__step--active': idx === sendPhaseIndex,
              'alex-progress__step--pending': idx > sendPhaseIndex
            }"
          >
            <span class="alex-progress__icon" aria-hidden="true">
              <i v-if="idx < sendPhaseIndex" class="fa fa-check"></i>
              <span v-else-if="idx === sendPhaseIndex" class="alex-progress__spinner"></span>
              <span v-else class="alex-progress__dot"></span>
            </span>
            <span class="alex-progress__label">{{ step.label }}</span>
          </li>
        </ol>
      </div>
    </div>

    <div class="alex-chat__composer" :class="{ 'alex-chat__composer--disabled': isBootstrapping }">
      <label class="alex-file">
        <input
          ref="fileInput"
          type="file"
          class="alex-file__input"
          :disabled="isBootstrapping || isSending"
          accept=".txt,.csv,.md,.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
          @change="onFileSelected"
        />
        <span class="alex-file__label">Attach file</span>
      </label>
      <div v-if="attachedFileName" class="alex-attached-file">
        <i class="fa fa-paperclip" aria-hidden="true"></i>
        <span class="alex-attached-file__name">{{ attachedFileName }}</span>
        <button
          type="button"
          class="alex-attached-file__remove"
          title="Remove file"
          aria-label="Remove attached file"
          :disabled="isBootstrapping || isSending"
          @click="removeAttachedFile"
        >
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </div>
      <textarea
        v-model="messageInput"
        class="alex-composer-input"
        rows="3"
        :placeholder="isBootstrapping ? 'Loading Tracey chat session…' : 'Describe changes or the form you want…'"
        :disabled="isSending || isBootstrapping"
      ></textarea>
      <button
        type="button"
        class="alex-btn alex-btn--send"
        :disabled="!selectedSessionId || isSending || isBootstrapping"
        @click="sendMessage"
      >
        <span v-if="isSending" class="alex-btn__loader" aria-hidden="true"></span>
        {{ isBootstrapping ? "Loading..." : (isSending ? sendPhaseLabel : "Send") }}
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "AIFormBuilderChatPanel",
  props: {
    surveyId: {
      type: [String, Number],
      required: true
    },
    /** When true, fills parent height (drawer body) */
    embedded: {
      type: Boolean,
      default: false
    },
    showSessionControls: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      sessions: [],
      selectedSessionId: null,
      messages: [],
      messageInput: "",
      generatedRaw: "",
      selectedFile: null,
      attachedFileName: "",
      attachedFileContent: "",
      isProcessingAttachment: false,
      showRawJson: false,
      isSending: false,
      isApplying: false,
      isBootstrapping: true,
      sendPhaseIndex: 0,
      sendProgressSteps: [],
      sendProgressHasLargeUpload: false,
      sendProgressStartedAt: 0,
      sendProgressMinDurationMs: 20000,
      sendPhaseTimer: null,
      traceyProgressImg: require("../../assets/images/tracey_svg.png"),
      editingSessionId: null,
      editTitleDraft: "",
      sessionDropdownOpen: false,
      isDragOver: false,
      dragDepth: 0,
      expandedMessagePreviewIds: {},
      greetingMessage:
        "Hi, I am Tracey. I can help you build survey forms quickly. Try prompts like: 'Create a motor accident survey form', 'Build a car service feedback survey', or 'Create a dog walking service enquiry survey with required contact details.'"
    };
  },
  watch: {
    surveyId(newVal, oldVal) {
      if (String(newVal || "") === String(oldVal || "")) {
        return;
      }
      this.bootstrapSurveySession();
    },
    messages() {
      this.$nextTick(() => this.scrollStreamToEnd());
    },
    generatedRaw() {
      if (!this.isSending) {
        this.$nextTick(() => this.scrollStreamToEnd());
      }
    },
    isSending(val) {
      if (val) {
        this.$nextTick(() => this.scrollStreamToEnd());
      }
    },
    sendPhaseIndex() {
      if (this.isSending) {
        this.$nextTick(() => this.scrollStreamToEnd());
      }
    }
  },
  computed: {
    ...mapGetters("LeftSidebarCreate", ["designall"]),
    /** Survey design tab colours (button / button text) scoped to Tracey chat. */
    chatThemeVars() {
      const d = this.designall && typeof this.designall === "object" ? this.designall : {};
      const vars = {};
      const buttonColor = this.normalizeThemeHex(d.button_color);
      const buttonTextColor = this.normalizeThemeHex(d.button_text_color);
      if (buttonColor) {
        vars["--buttonLinkColor"] = buttonColor;
      }
      if (buttonTextColor) {
        vars["--buttonTextColor"] = buttonTextColor;
      }
      return vars;
    },
    showGeneratedPreview() {
      return !this.isSending && !!(this.generatedRaw && String(this.generatedRaw).trim());
    },
    displayMessages() {
      if (!this.selectedSessionId) {
        return this.messages;
      }
      let list = [];
      if (Array.isArray(this.messages) && this.messages.length > 0) {
        list = this.messages;
      } else {
        return [
          {
            in_chat_message_id: "tracey-greeting-" + this.selectedSessionId,
            st_role: "assistant",
            st_message: this.greetingMessage
          }
        ];
      }
      if (this.isSending) {
        list = list.slice();
        while (list.length > 0) {
          const last = list[list.length - 1];
          const role = String((last && last.st_role) || "").toLowerCase();
          if (role === "assistant" && this.messageFormPreview(last.st_message)) {
            list.pop();
            continue;
          }
          break;
        }
      }
      return list;
    },
    generatedParsed() {
      try {
        return JSON.parse(this.generatedRaw || "{}");
      } catch (e) {
        return null;
      }
    },
    generatedQuestions() {
      if (!this.generatedParsed || !Array.isArray(this.generatedParsed.questions)) {
        return [];
      }
      return this.generatedParsed.questions;
    },
    hasAppliedFormMappings() {
      const parsed = this.generatedParsed;
      if (!parsed) {
        return false;
      }
      if (parsed.apply_synced) {
        return true;
      }
      if (Array.isArray(parsed.applied_mappings) && parsed.applied_mappings.length > 0) {
        return true;
      }
      return this.generatedQuestions.some((q) => {
        return q && (q.in_question_master_id || q.tapapp_question_id);
      });
    },
    applyButtonLabel() {
      if (this.isApplying) {
        return this.hasAppliedFormMappings ? "Updating form…" : "Applying to form…";
      }
      return this.hasAppliedFormMappings ? "Update form" : "Apply to form";
    },
    generatedFormTitle() {
      if (!this.generatedParsed || !this.generatedParsed.form) {
        return "Generated Form";
      }
      return this.generatedParsed.form.title || "Generated Form";
    },
    generatedQuestionCount() {
      return this.generatedQuestions.length;
    },
    generatedSkipLogic() {
      if (!this.generatedParsed || !Array.isArray(this.generatedParsed.skip_logic)) {
        return [];
      }
      return this.generatedParsed.skip_logic;
    },
    sendPhaseLabel() {
      if (!this.isSending || !Array.isArray(this.sendProgressSteps) || this.sendProgressSteps.length === 0) {
        return "Working…";
      }
      const idx = Math.min(this.sendPhaseIndex, this.sendProgressSteps.length - 1);
      const step = this.sendProgressSteps[idx];
      return step && step.label ? step.label : "Working…";
    },
    generatedSkipLogicCount() {
      return this.generatedSkipLogic.length;
    }
  },
  mounted() {
    document.addEventListener("click", this.onDocumentClickSessionDropdown);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onDocumentClickSessionDropdown);
    this.stopSendProgress();
  },
  async created() {
    await this.bootstrapSurveySession();
  },
  methods: {
    normalizeThemeHex(color) {
      if (!color || typeof color !== "string") {
        return "";
      }
      const trimmed = color.trim();
      if (!trimmed) {
        return "";
      }
      return trimmed.charAt(0) === "#" ? trimmed : "#" + trimmed;
    },
    sessionStorageKey() {
      return "tracey:selectedSession:" + String(this.surveyId || "global");
    },
    getStoredSessionId() {
      try {
        const raw = window.localStorage.getItem(this.sessionStorageKey());
        if (!raw) {
          return null;
        }
        const parsed = parseInt(raw, 10);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
      } catch (e) {
        return null;
      }
    },
    setStoredSessionId(sessionId) {
      try {
        if (!sessionId) {
          window.localStorage.removeItem(this.sessionStorageKey());
          return;
        }
        window.localStorage.setItem(this.sessionStorageKey(), String(sessionId));
      } catch (e) {
        /* ignore storage errors */
      }
    },
    async bootstrapSurveySession() {
      this.isBootstrapping = true;
      this.clearLocalChatState();
      this.selectedSessionId = null;
      try {
        await this.loadSessions();
        const storedId = this.getStoredSessionId();
        let target = null;
        if (storedId) {
          target = this.sessions.find((s) => s && s.in_chat_session_id === storedId) || null;
        }
        if (!target && this.sessions.length) {
          target = this.sessions[0];
        }
        if (target) {
          await this.selectSession(target);
          return;
        }
        await this.createSession(true);
      } catch (e) {
        console.error("Tracey: failed to bootstrap survey session", e);
        this.$awn.alert("Unable to open chat session. Please refresh and try again.");
      } finally {
        this.isBootstrapping = false;
      }
    },
    bubbleKind(role) {
      const r = (role || "").toLowerCase();
      if (r === "user") return "user";
      if (r === "assistant" || r === "system") return "assistant";
      return "assistant";
    },
    bubbleClasses(msg) {
      const base = "alex-bubble--" + this.bubbleKind(msg.st_role);
      if (this.isErrorMsg(msg)) {
        return [base, "alex-bubble--error"];
      }
      if (this.isClarificationMsg(msg)) {
        return [base, "alex-bubble--clarify"];
      }
      return [base];
    },
    isErrorMsg(msg) {
      let meta = msg && msg.st_metadata_json;
      if (meta) {
        if (typeof meta === "string") {
          try {
            meta = JSON.parse(meta);
          } catch (e) {
            meta = null;
          }
        }
        if (meta && meta.type === "error") {
          return true;
        }
      }
      const raw = String((msg && msg.st_message) || "").trim();
      if (!raw || raw.charAt(0) !== "{") {
        return false;
      }
      try {
        const parsed = JSON.parse(raw);
        return !!(parsed && (parsed.error || parsed.type === "error"));
      } catch (e) {
        return false;
      }
    },
    rebrandTraceyMessage(text) {
      let out = String(text || "");
      out = out.replace(/The AI service/gi, "Tracey");
      out = out.replace(/the AI service/gi, "Tracey");
      out = out.replace(/AI response/gi, "response");
      out = out.replace(/configured for AI/gi, "set up");
      out = out.replace(/Something went wrong while building your form/gi, "Tracey could not build your form right now");
      return out;
    },
    humanizeErrorText(technical) {
      const text = String(technical || "").trim();
      const lower = text.toLowerCase();
      if (!text) {
        return "Tracey could not build your form right now. Please try again in a moment.";
      }
      if (lower.indexOf("overloaded") !== -1 || lower.indexOf("529") !== -1) {
        return "Tracey is busy right now. Please wait a minute and try again.";
      }
      if (lower.indexOf("rate_limit") !== -1 || lower.indexOf("429") !== -1) {
        return "Tracey is receiving too many requests. Please wait a moment and try again.";
      }
      if (
        lower.indexOf("timed out") !== -1 ||
        lower.indexOf("timeout") !== -1 ||
        lower.indexOf("econnaborted") !== -1
      ) {
        return "This took too long to complete. Try again, use a smaller attachment, or split the work (for example: generate the form first, then add skip logic in a second message).";
      }
      if (lower.indexOf("max_tokens") !== -1 || lower.indexOf("truncated") !== -1) {
        return "The form was too large to finish in one step. Try a shorter checklist or generate in smaller parts.";
      }
      if (lower.indexOf("not valid json") !== -1 || lower.indexOf("invalid json") !== -1) {
        return "Tracey could not finish the response. Please try sending your message again.";
      }
      if (lower.indexOf("claude_api_key") !== -1 || lower.indexOf("api key") !== -1) {
        return "Tracey is not set up on this server. Please contact your administrator.";
      }
      return "Tracey could not build your form right now. Please try again in a moment.";
    },
    formatDisplayMessage(msg) {
      if (this.isErrorMsg(msg)) {
        const raw = String((msg && msg.st_message) || "").trim();
        if (raw.charAt(0) === "{") {
          try {
            const parsed = JSON.parse(raw);
            if (parsed && parsed.error) {
              return this.rebrandTraceyMessage(this.humanizeErrorText(parsed.error));
            }
          } catch (e) {
            /* use plain text below */
          }
        }
        return this.rebrandTraceyMessage(
          raw || "Tracey could not build your form right now. Please try again in a moment."
        );
      }
      return String((msg && msg.st_message) || "");
    },
    resolveSendErrorMessage(res, err) {
      if (res && res.data && res.data.msg) {
        return this.rebrandTraceyMessage(this.humanizeErrorText(res.data.msg));
      }
      if (err && err.response && err.response.data && err.response.data.msg) {
        return this.rebrandTraceyMessage(this.humanizeErrorText(err.response.data.msg));
      }
      const timedOut =
        err &&
        (err.code === "ECONNABORTED" ||
          (err.message && String(err.message).toLowerCase().indexOf("timeout") !== -1));
      if (timedOut) {
        return "This took too long to complete. Try again, use a smaller attachment, or split the work (for example: generate the form first, then add skip logic in a second message).";
      }
      if (err && err.message) {
        return this.rebrandTraceyMessage(this.humanizeErrorText(err.message));
      }
      return "Tracey could not build your form right now. Please try again in a moment.";
    },
    isClarificationMsg(msg) {
      let meta = msg && msg.st_metadata_json;
      if (!meta) {
        return false;
      }
      if (typeof meta === "string") {
        try {
          meta = JSON.parse(meta);
        } catch (e) {
          return false;
        }
      }
      return meta && meta.type === "clarification";
    },
    labelRole(role) {
      const r = (role || "").toLowerCase();
      if (r === "user") return "You";
      if (r === "assistant") return "Tracey";
      return role || "Message";
    },
    questionTitle(question) {
      if (!question || typeof question !== "object") {
        return "Untitled question";
      }
      return question.title || question.question || "Untitled question";
    },
    questionType(question) {
      if (!question || typeof question !== "object") {
        return "unknown";
      }
      return question.question_type_id || question.control_id || "unknown";
    },
    isQuestionRequired(question) {
      if (!question || typeof question !== "object") {
        return false;
      }
      if (question.validations && typeof question.validations === "object") {
        if (question.validations.required === true || question.validations.required === "true" || question.validations.required === 1) {
          return true;
        }
      }
      if (Array.isArray(question.validations)) {
        return question.validations.some(function (v) {
          return v === "required" || (v && v.type === "required") || (v && v.rule === "required");
        });
      }
      return false;
    },
    previewMessageKey(msg) {
      if (!msg) {
        return "";
      }
      return String(msg.in_chat_message_id || "");
    },
    isMessagePreviewExpanded(msg) {
      const key = this.previewMessageKey(msg);
      if (!key) {
        return false;
      }
      return !!this.expandedMessagePreviewIds[key];
    },
    toggleMessagePreview(msg) {
      const key = this.previewMessageKey(msg);
      if (!key) {
        return;
      }
      const next = Object.assign({}, this.expandedMessagePreviewIds);
      next[key] = !next[key];
      this.expandedMessagePreviewIds = next;
      this.$nextTick(() => this.scrollStreamToEnd());
    },
    messageFormPreview(rawText) {
      const text = (rawText || "").trim();
      if (!text) {
        return null;
      }
      let parsed = null;
      try {
        parsed = JSON.parse(text);
      } catch (e) {
        const start = text.indexOf("{");
        const end = text.lastIndexOf("}");
        if (start !== -1 && end !== -1 && end > start) {
          try {
            parsed = JSON.parse(text.slice(start, end + 1));
          } catch (e2) {
            parsed = null;
          }
        }
      }
      if (
        parsed &&
        typeof parsed === "object" &&
        parsed.form &&
        Array.isArray(parsed.questions)
      ) {
        return parsed;
      }
      return null;
    },
    scrollStreamToEnd() {
      this.$nextTick(() => {
        const el = this.$refs.stream;
        if (!el) {
          return;
        }
        el.scrollTop = el.scrollHeight;
        if (this.isSending && this.$refs.sendProgress) {
          this.$refs.sendProgress.scrollIntoView({ block: "end", behavior: "auto" });
        }
      });
    },
    onDocumentClickSessionDropdown(ev) {
      if (!this.sessionDropdownOpen) {
        return;
      }
      if (this.editingSessionId !== null) {
        return;
      }
      const root = this.$refs.sessionDropdownRoot;
      if (root && root.contains(ev.target)) {
        return;
      }
      this.sessionDropdownOpen = false;
    },
    toggleSessionDropdown() {
      if (this.editingSessionId !== null) {
        return;
      }
      this.sessionDropdownOpen = !this.sessionDropdownOpen;
    },
    closeSessionDropdown() {
      this.sessionDropdownOpen = false;
    },
    pickSession(session) {
      if (this.editingSessionId !== null) {
        return;
      }
      if (session) {
        this.selectSession(session);
        this.closeSessionDropdown();
      }
    },
    startSessionRename(session) {
      this.editingSessionId = session.in_chat_session_id;
      this.editTitleDraft = session.st_session_title || "";
      this.$nextTick(() => {
        const list = this.$el.querySelector(".alex-session-input");
        if (list) list.focus();
      });
    },
    cancelSessionRename() {
      this.editingSessionId = null;
      this.editTitleDraft = "";
    },
    async saveSessionRename(session) {
      const title = (this.editTitleDraft || "").trim();
      if (!title) {
        this.$awn.alert("Session name cannot be empty.");
        return;
      }
      try {
        let res = await axios.post(
          process.env.API_PATH + "/chatbot/session/rename",
          Object.assign({}, this.basePayload(), {
            in_chat_session_id: session.in_chat_session_id,
            title: title
          }),
          this.authHeaders()
        );
        if (res.data.flag === 1) {
          this.cancelSessionRename();
          await this.loadSessions();
          this.$awn.success("Session name updated.");
        } else {
          this.$awn.alert(res.data.msg || "Could not rename session.");
        }
      } catch (e) {
        this.$awn.alert("Could not rename session.");
      }
    },
    async deleteSession(session) {
      if (this.editingSessionId !== null) {
        return;
      }
      if (
        !window.confirm(
          "Delete this chat session? Its messages will no longer appear in the list."
        )
      ) {
        return;
      }
      const deletedId = session.in_chat_session_id;
      try {
        let res = await axios.post(
          process.env.API_PATH + "/chatbot/session/delete",
          Object.assign({}, this.basePayload(), {
            in_chat_session_id: deletedId
          }),
          this.authHeaders()
        );
        if (res.data.flag === 1) {
          if (this.editingSessionId === deletedId) {
            this.cancelSessionRename();
          }
          await this.loadSessions();
          if (this.selectedSessionId === deletedId) {
            this.setStoredSessionId(null);
            if (this.sessions.length) {
              await this.selectSession(this.sessions[0]);
            } else {
              await this.createSession();
            }
          }
          this.closeSessionDropdown();
          this.$awn.success("Session deleted.");
        } else {
          this.$awn.alert(res.data.msg || "Could not delete session.");
        }
      } catch (e) {
        this.$awn.alert("Could not delete session.");
      }
    },
    authHeaders() {
      return {
        headers: {
          "Content-Type": "application/json",
          Authorization: this.$session.get("jwt_token")
        }
      };
    },
    basePayload() {
      return {
        lang: this.$session.get("lang"),
        request_id: this.$session.get("requestId")
      };
    },
    async loadSessions() {
      let res = await axios.post(
        process.env.API_PATH + "/chatbot/session/list",
        Object.assign({}, this.basePayload(), {
          in_survey_Id: this.surveyId
        }),
        this.authHeaders()
      );
      if (res.data.flag === 1) {
        this.sessions = res.data.data || [];
      }
    },
    clearLocalChatState() {
      this.stopSendProgress();
      this.isSending = false;
      this.messages = [];
      this.expandedMessagePreviewIds = {};
      this.messageInput = "";
      this.generatedRaw = "";
      this.showRawJson = false;
      this.removeAttachedFile();
    },
    async createSession(forceDuringBootstrap = false) {
      if (this.isBootstrapping && !forceDuringBootstrap) {
        return;
      }
      this.clearLocalChatState();
      let payload = Object.assign({}, this.basePayload(), {
        title: "New chat",
        in_survey_Id: this.surveyId
      });
      try {
        let res = await axios.post(
          process.env.API_PATH + "/chatbot/session/create",
          payload,
          this.authHeaders()
        );
        if (res.data.flag === 1) {
          await this.loadSessions();
          await this.selectSession(res.data.data);
        } else {
          console.error("Tracey: createSession returned", res.data);
          this.$awn.alert(res.data.msg || "Unable to create session.");
        }
      } catch (e) {
        console.error("Tracey: createSession error", e);
        this.$awn.alert("Unable to create session. Check console for details.");
      }
    },
    async selectSession(session) {
      this.selectedSessionId = session.in_chat_session_id;
      this.setStoredSessionId(this.selectedSessionId);
      this.showRawJson = false;
      let res = await axios.post(
        process.env.API_PATH + "/chatbot/history",
        Object.assign({}, this.basePayload(), {
          in_chat_session_id: this.selectedSessionId
        }),
        this.authHeaders()
      );
      this.messages = res.data.flag === 1 ? (res.data.data || []) : [];
      this.syncGeneratedRawFromSessionMessages();
    },
    syncGeneratedRawFromSessionMessages() {
      var list = Array.isArray(this.messages) ? this.messages : [];
      for (var i = list.length - 1; i >= 0; i--) {
        var m = list[i];
        if (!m || String(m.st_role || "").toLowerCase() !== "assistant") {
          continue;
        }
        var meta = m.st_metadata_json;
        if (typeof meta === "string") {
          try {
            meta = JSON.parse(meta);
          } catch (eMeta) {
            meta = null;
          }
        }
        if (
          meta &&
          (meta.type === "non_form_prompt" ||
            meta.type === "clarification" ||
            meta.type === "error")
        ) {
          this.generatedRaw = "";
          return;
        }
        var raw = String(m.st_message || "").trim();
        if (!raw || raw.charAt(0) !== "{") {
          this.generatedRaw = "";
          return;
        }
        try {
          var parsed = JSON.parse(raw);
          if (
            parsed &&
            parsed.type === "non_form_prompt"
          ) {
            this.generatedRaw = "";
            return;
          }
          if (parsed && parsed.response_type === "clarification") {
            this.generatedRaw = "";
            return;
          }
          if (
            parsed &&
            (Array.isArray(parsed.questions) ||
              (parsed.form && typeof parsed.form === "object"))
          ) {
            this.generatedRaw = JSON.stringify(parsed, null, 2);
            return;
          }
        } catch (e) {
          this.generatedRaw = "";
          return;
        }
      }
      this.generatedRaw = "";
    },
    isImageFile(fileName) {
      return /\.(png|jpe?g)$/i.test(fileName || "");
    },
    allowedFileExtensions() {
      return [
        "txt",
        "csv",
        "md",
        "pdf",
        "doc",
        "docx",
        "xls",
        "xlsx",
        "png",
        "jpg",
        "jpeg"
      ];
    },
    supportedFileTypeLabel() {
      return ".txt, .csv, .md, .pdf, .doc, .docx, .xls, .xlsx, .png, .jpg, .jpeg";
    },
    maxAllowedFileBytes() {
      return 5 * 1024 * 1024;
    },
    getFileExtension(fileName) {
      const n = String(fileName || "").toLowerCase().trim();
      const idx = n.lastIndexOf(".");
      if (idx < 0 || idx === n.length - 1) {
        return "";
      }
      return n.slice(idx + 1);
    },
    isAllowedFileType(fileName) {
      const ext = this.getFileExtension(fileName);
      if (!ext) {
        return false;
      }
      return this.allowedFileExtensions().indexOf(ext) !== -1;
    },
    isBinaryFile(fileName) {
      return /\.(pdf|docx?|xlsx?|png|jpe?g)$/i.test(fileName || "");
    },
    dragHasFiles(ev) {
      if (!ev || !ev.dataTransfer) {
        return false;
      }
      if (ev.dataTransfer.files && ev.dataTransfer.files.length > 0) {
        return true;
      }
      const types = ev.dataTransfer.types;
      if (!types) {
        return false;
      }
      return Array.from(types).indexOf("Files") !== -1;
    },
    canAcceptDropNow() {
      return !this.isBootstrapping && !this.isSending;
    },
    onChatDragEnter(ev) {
      if (!this.dragHasFiles(ev)) {
        return;
      }
      if (!this.canAcceptDropNow()) {
        return;
      }
      this.dragDepth += 1;
      this.isDragOver = true;
    },
    onChatDragOver(ev) {
      if (!this.dragHasFiles(ev)) {
        return;
      }
      if (ev.dataTransfer) {
        ev.dataTransfer.dropEffect = this.canAcceptDropNow() ? "copy" : "none";
      }
      if (!this.canAcceptDropNow()) {
        return;
      }
      this.isDragOver = true;
    },
    onChatDragLeave(ev) {
      if (!this.dragHasFiles(ev)) {
        return;
      }
      this.dragDepth = Math.max(0, this.dragDepth - 1);
      if (this.dragDepth === 0) {
        this.isDragOver = false;
      }
    },
    onChatDrop(ev) {
      this.dragDepth = 0;
      this.isDragOver = false;
      if (!this.dragHasFiles(ev) || !this.canAcceptDropNow()) {
        return;
      }
      const files = ev.dataTransfer && ev.dataTransfer.files ? ev.dataTransfer.files : [];
      if (!files || files.length < 1) {
        return;
      }
      this.processSelectedFile(files[0]);
    },
    processSelectedFile(file) {
      if (!file) {
        return;
      }
      if ((file.size || 0) > this.maxAllowedFileBytes()) {
        this.$awn.alert("File must be under 5 MB.");
        return;
      }
      if (!this.isAllowedFileType(file.name)) {
        this.$awn.alert(
          "Unsupported file type. Supported file types: " + this.supportedFileTypeLabel()
        );
        return;
      }
      if (!this.selectedSessionId) {
        this.$awn.alert("Start or select a chat first.");
        return;
      }
      this.attachedFileName = file.name;
      if (this.isBinaryFile(file.name)) {
        this.isProcessingAttachment = true;
        this.extractFileViaServer(file);
      } else {
        const reader = new FileReader();
        reader.onload = (evt) => {
          this.attachedFileContent = evt.target.result || "";
          this.$awn.success("File attached: " + file.name);
        };
        reader.onerror = () => {
          this.$awn.alert("Could not read file.");
          this.removeAttachedFile();
        };
        reader.readAsText(file);
      }
    },
    onFileSelected(e) {
      const file = e.target.files[0];
      if (!file) return;
      this.processSelectedFile(file);
    },
    async extractFileViaServer(file) {
      const form = new FormData();
      form.append("file", file);
      form.append("lang", this.$session.get("lang"));
      form.append("request_id", this.$session.get("requestId"));
      try {
        let res = await axios.post(
          process.env.API_PATH + "/chatbot/attachment/ocr",
          form,
          {
            headers: {
              Authorization: this.$session.get("jwt_token"),
              "Content-Type": "multipart/form-data"
            }
          }
        );
        if (res.data.flag === 1 && res.data.data && res.data.data.text) {
          this.attachedFileContent = res.data.data.text;
          this.$awn.success("File processed: " + file.name);
        } else {
          this.pushLocalAssistantMessage(
            this.humanizeErrorText(res.data.msg || "Tracey could not read your attached file."),
            "error"
          );
          this.removeAttachedFile();
        }
      } catch (e) {
        this.pushLocalAssistantMessage(
          "Tracey could not process your file. Try a .txt, .md, or .csv checklist.",
          "error"
        );
        this.removeAttachedFile();
      } finally {
        this.isProcessingAttachment = false;
      }
    },
    removeAttachedFile() {
      this.attachedFileName = "";
      this.attachedFileContent = "";
      this.isProcessingAttachment = false;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },
    buildSendProgressSteps(hasAttachment) {
      if (hasAttachment) {
        return [
          { id: "prepare", label: "Preparing request" },
          { id: "upload", label: "Uploading form data" },
          { id: "investigate", label: "Investigating document" },
          { id: "plan", label: "Planning form structure" },
          { id: "generate", label: "Generating questions" }
        ];
      }
      return [
        { id: "send", label: "Sending your message" },
        { id: "investigate", label: "Investigating requirements" },
        { id: "structure", label: "Generating form structure" },
        { id: "questions", label: "Building questions & options" }
      ];
    },
    startSendProgress(hasAttachment) {
      this.stopSendProgress();
      this.sendProgressStartedAt = Date.now();
      this.sendProgressHasLargeUpload = hasAttachment;
      this.sendProgressSteps = this.buildSendProgressSteps(hasAttachment);
      this.sendPhaseIndex = 0;
      const stepCount = this.sendProgressSteps.length;
      const transitions = Math.max(1, stepCount - 1);
      const intervalMs = Math.max(
        4000,
        Math.floor((this.sendProgressMinDurationMs - 2000) / transitions)
      );
      this.sendPhaseTimer = setInterval(() => {
        const maxIdx = this.sendProgressSteps.length - 1;
        if (this.sendPhaseIndex < maxIdx) {
          this.sendPhaseIndex += 1;
          this.$nextTick(() => this.scrollStreamToEnd());
        }
      }, intervalMs);
      this.$nextTick(() => this.scrollStreamToEnd());
    },
    delay(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
    pushLocalAssistantMessage(text, type = "info") {
      this.messages = Array.isArray(this.messages) ? this.messages.slice() : [];
      this.messages.push({
        in_chat_message_id: "local-" + type + "-" + Date.now(),
        st_role: "assistant",
        st_message: String(text || ""),
        st_metadata_json: JSON.stringify({ type })
      });
    },
    async finishSendProgress() {
      if (this.sendPhaseTimer) {
        clearInterval(this.sendPhaseTimer);
        this.sendPhaseTimer = null;
      }
      const steps = this.sendProgressSteps;
      const maxIdx = steps.length > 0 ? steps.length - 1 : 0;
      const startedAt = this.sendProgressStartedAt || Date.now();
      const minMs = this.sendProgressMinDurationMs;

      while (this.sendPhaseIndex < maxIdx) {
        const elapsed = Date.now() - startedAt;
        const remaining = minMs - elapsed;
        if (remaining <= 0) {
          this.sendPhaseIndex = maxIdx;
          break;
        }
        const stepsLeft = maxIdx - this.sendPhaseIndex;
        const waitMs = Math.min(remaining, Math.max(400, Math.floor(remaining / stepsLeft)));
        await this.delay(waitMs);
        if (this.sendPhaseIndex < maxIdx) {
          this.sendPhaseIndex += 1;
          this.$nextTick(() => this.scrollStreamToEnd());
        }
      }

      const elapsed = Date.now() - startedAt;
      const remaining = minMs - elapsed;
      if (remaining > 0) {
        await this.delay(remaining);
      }

      this.stopSendProgress();
    },
    stopSendProgress() {
      if (this.sendPhaseTimer) {
        clearInterval(this.sendPhaseTimer);
        this.sendPhaseTimer = null;
      }
      this.sendPhaseIndex = 0;
      this.sendProgressSteps = [];
      this.sendProgressHasLargeUpload = false;
      this.sendProgressStartedAt = 0;
    },
    async sendMessage() {
      if (!this.selectedSessionId) {
        this.pushLocalAssistantMessage("Please start or select a chat session first.", "error");
        return;
      }
      if (this.isSending) {
        return;
      }
      if (!this.messageInput.trim() && !this.attachedFileContent) {
        this.pushLocalAssistantMessage("Message cannot be blank.", "error");
        return;
      }
      if (this.isProcessingAttachment) {
        this.pushLocalAssistantMessage(
          "Tracey is still reading your file. Wait until processing finishes, then send.",
          "error"
        );
        return;
      }
      if (this.attachedFileName && !(this.attachedFileContent || "").trim()) {
        this.pushLocalAssistantMessage(
          "Tracey could not read your attached file. Re-attach the file or use a .txt, .md, or .csv checklist.",
          "error"
        );
        return;
      }

      const visibleMessage = this.messageInput.trim();
      const attachmentContext = (this.attachedFileContent || "").trim();
      const attachmentFileName = this.attachedFileName || "";
      const hasAttachment = attachmentContext.length > 0;
      const localUserMessage =
        visibleMessage !== ""
          ? visibleMessage
          : attachmentFileName !== ""
            ? "Attached file: " + attachmentFileName
            : "Attached file";
      this.messages = Array.isArray(this.messages) ? this.messages.slice() : [];
      this.messages.push({
        in_chat_message_id: "local-user-" + Date.now(),
        st_role: "user",
        st_message: localUserMessage
      });
      this.messageInput = "";
      if (attachmentContext.length > 0) {
        this.removeAttachedFile();
      }
      this.isSending = true;
      this.startSendProgress(hasAttachment);
      let pendingGeneratedRaw = null;
      let refreshSessionAfterSend = false;
      try {
        const payload = Object.assign({}, this.basePayload(), {
          in_chat_session_id: this.selectedSessionId,
          message: visibleMessage,
          attachment_file_name: attachmentFileName,
          attachment_context: attachmentContext
        });
        let res = await axios.post(
          process.env.API_PATH + "/chatbot/message/send",
          payload,
          Object.assign({}, this.authHeaders(), {
            timeout: 600000
          })
        );
        refreshSessionAfterSend = true;
        if (res.data.flag !== 1) {
          pendingGeneratedRaw = "";
        } else if (
          res.data.data &&
          res.data.data.type === "non_form_prompt"
        ) {
          pendingGeneratedRaw = "";
        } else if (
          res.data.data &&
          (res.data.data.type === "clarification" ||
            res.data.data.response_type === "clarification")
        ) {
          pendingGeneratedRaw = "";
        } else if (res.data.data) {
          pendingGeneratedRaw = JSON.stringify(res.data.data, null, 2);
        } else {
          pendingGeneratedRaw = "";
        }
        this.showRawJson = false;
      } catch (e) {
        pendingGeneratedRaw = "";
        this.pushLocalAssistantMessage(
          this.resolveSendErrorMessage(null, e),
          "error"
        );
      } finally {
        await this.finishSendProgress();
        if (pendingGeneratedRaw !== null) {
          this.generatedRaw = pendingGeneratedRaw;
        }
        if (refreshSessionAfterSend) {
          await this.loadSessions();
          await this.selectSession({ in_chat_session_id: this.selectedSessionId });
        }
        this.isSending = false;
        this.$nextTick(() => this.scrollStreamToEnd());
      }
    },
    async applyGenerated() {
      if (this.isApplying) {
        return;
      }
      if (!this.selectedSessionId) {
        this.$awn.alert("No active session found.");
        return;
      }
      let generated = {};
      try {
        generated = JSON.parse(this.generatedRaw || "{}");
      } catch (e) {
        this.$awn.alert("Generated output is not valid JSON.");
        return;
      }
      if (
        generated.response_type === "clarification" ||
        generated.type === "clarification"
      ) {
        this.$awn.alert("Apply is only available after Tracey generates a form, not for clarification messages.");
        return;
      }
      let payload = Object.assign({}, this.basePayload(), {
        in_survey_Id: this.surveyId,
        generated_form: generated
      });
      this.isApplying = true;
      try {
        let res = await axios.post(
          process.env.API_PATH + "/chatbot/form/apply",
          payload,
          this.authHeaders()
        );
        if (res.data.flag === 1) {
          const enriched =
            res.data.data &&
            res.data.data.generated_form &&
            typeof res.data.data.generated_form === "object"
              ? res.data.data.generated_form
              : null;
          if (enriched) {
            this.generatedRaw = JSON.stringify(enriched, null, 2);
          } else {
            this.mergeApplyMappingsIntoGenerated(res.data.data);
          }
          this.$awn.success(res.data.msg || "Form updated successfully.");
          this.$emit("applied");
        } else {
          this.$awn.alert(res.data.msg || "Failed to apply generated form.");
        }
      } catch (e) {
        this.$awn.alert("Failed to apply generated form. Please try again.");
      } finally {
        this.isApplying = false;
      }
    },
    mergeApplyMappingsIntoGenerated(applyData) {
      if (!applyData || !Array.isArray(applyData.questions)) {
        return;
      }
      let parsed = {};
      try {
        parsed = JSON.parse(this.generatedRaw || "{}");
      } catch (e) {
        return;
      }
      if (!Array.isArray(parsed.questions)) {
        return;
      }
      const mappings = [];
      applyData.questions.forEach((row, idx) => {
        if (!parsed.questions[idx] || !row || !row.in_question_master_id) {
          return;
        }
        parsed.questions[idx].in_question_master_id = row.in_question_master_id;
        mappings.push({
          index: idx,
          in_question_master_id: row.in_question_master_id
        });
      });
      parsed.applied_mappings = mappings;
      parsed.apply_synced = true;
      this.generatedRaw = JSON.stringify(parsed, null, 2);
    }
  }
};
</script>

<style scoped>
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

.alex-chat {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 320px;
  height: 100%;
  background: #f4f6f9;
}

.alex-chat--embedded {
  min-height: 0;
}

.alex-drop-overlay {
  position: absolute;
  inset: 10px;
  z-index: 80;
  border: 2px dashed var(--buttonLinkColor);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.88);
  color: var(--buttonLinkColor);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  pointer-events: none;
}

.alex-chat__toolbar {
  flex-shrink: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  padding: 10px 12px;
  background: var(--headerColor);
  border-bottom: 1px solid #e2e8f0;
}

.alex-toolbar-label {
  flex: 1;
  min-width: 120px;
  margin: 0;
}

.alex-session-panel {
  position: relative;
  z-index: 30;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.alex-session-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 7px 10px;
  font-size: 13px;
  color: var(--headerTextColor);
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  text-align: left;
  box-sizing: border-box;
}

.alex-session-trigger:hover {
  border-color: #94a3b8;
  background: #fafafa;
}

.alex-session-trigger__text {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.alex-session-trigger__caret {
  flex-shrink: 0;
  font-size: 11px;
  color: var(--headerTextColor);
  transition: transform 0.15s ease;
}

.alex-session-trigger__caret--open {
  transform: rotate(180deg);
}

.alex-session-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  background: #fff;
  box-shadow: 0 10px 25px -5px rgba(15, 23, 42, 0.14);
  overflow: hidden;
}

.alex-session-list {
  max-height: 240px;
  overflow-y: auto;
  background: #fff;
}

.alex-session-row {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 6px;
  border-bottom: 1px solid #f1f5f9;
}

.alex-session-row:last-child {
  border-bottom: none;
}

.alex-session-row--active {
  background: #f0f9ff;
}

.alex-session-select {
  flex: 1;
  min-width: 0;
  text-align: left;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  color: #1e293b;
  padding: 6px 4px;
  border-radius: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.alex-session-select:hover {
  background: #f8fafc;
}

.alex-session-select--active {
  font-weight: 600;
  color: #0f172a;
}

.alex-session-pencil,
.alex-session-delete,
.alex-session-icon {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.alex-session-pencil:hover,
.alex-session-icon:hover {
  background: #e2e8f0;
  color: #0f172a;
}

.alex-session-delete:hover {
  background: #fee2e2;
  color: #b91c1c;
}

.alex-session-icon--save {
  color: #15803d;
}

.alex-session-icon--save:hover {
  background: #dcfce7;
  color: #14532d;
}

.alex-session-input {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  padding: 6px 8px;
  border: 1px solid #94a3b8;
  border-radius: 6px;
}

.alex-chat__stream {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px;
}

.alex-chat__hint {
  font-size: 13px;
  color: #64748b;
  margin: 0 0 12px;
  line-height: 1.45;
}

.alex-progress--stream-end {
  margin-top: 16px;
}

.alex-progress {
  margin: 0 0 14px;
  padding: 14px;
  border-radius: 12px;
  border: 1px solid #c7d2fe;
  background: linear-gradient(180deg, #f8faff 0%, #eef2ff 100%);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
}

.alex-progress__header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.alex-progress__avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px #c7d2fe;
}

.alex-progress__title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.alex-progress__subtitle {
  margin: 2px 0 0;
  font-size: 12px;
  color: #4f46e5;
}

.alex-progress__steps {
  list-style: none;
  margin: 0;
  padding: 0;
}

.alex-progress__step {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
  font-size: 13px;
  color: #64748b;
}

.alex-progress__step--active {
  color: #1e293b;
  font-weight: 600;
}

.alex-progress__step--done {
  color: #334155;
}

.alex-progress__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.alex-progress__step--done .alex-progress__icon {
  color: #16a34a;
  font-size: 12px;
}

.alex-progress__spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid #c7d2fe;
  border-top-color: #4f46e5;
  animation: alex-spin 0.7s linear infinite;
}

.alex-progress__dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #cbd5e1;
}

.alex-progress__hint {
  margin: 10px 0 0;
  padding-top: 10px;
  border-top: 1px solid #e0e7ff;
  font-size: 11px;
  color: #64748b;
  line-height: 1.4;
}

.alex-bubble {
  max-width: 100%;
  margin-bottom: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #fff;
}

.alex-bubble--user {
  margin-left: 24px;
  background: var(--buttonLinkColor);
  border-color: var(--buttonLinkColor);
  color: var(--buttonTextColor);
}

.alex-bubble--user .alex-bubble__role,
.alex-bubble--user .alex-bubble__text {
  color: var(--buttonTextColor);
}

.alex-bubble--assistant {
  margin-right: 16px;
  background: #fff;
}

.alex-bubble--clarify {
  border-color: #c4b5fd;
  background: #f5f3ff;
}

.alex-bubble--error {
  border-color: #fecaca;
  background: #fef2f2;
}

.alex-bubble--error .alex-bubble__role {
  color: #b91c1c;
}

.alex-bubble--error .alex-bubble__text {
  color: #7f1d1d;
}

.alex-bubble--json {
  margin-right: 0;
  background: #fafbfc;
  border-color: #cbd5e1;
}

.alex-bubble__role {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  margin-bottom: 6px;
}

.alex-bubble__text {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: ui-monospace, Menlo, Consolas, monospace;
  font-size: 12px;
  line-height: 1.45;
  color: #1e293b;
}

.alex-json-hint {
  margin: 0 0 8px;
  font-size: 12px;
  color: #64748b;
}

.alex-preview {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px;
  margin-bottom: 10px;
}

.alex-preview__title {
  margin: 0;
  font-size: 15px;
  color: #0f172a;
}

.alex-preview__meta {
  margin: 4px 0 10px;
  font-size: 12px;
  color: #64748b;
}

.alex-preview__toggle {
  width: 100%;
  text-align: left;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
}

.alex-preview__toggle-label {
  display: inline-block;
  margin-top: 2px;
  font-size: 11px;
  font-weight: 600;
  color: #4a90d9;
}

.alex-preview__expanded {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #e2e8f0;
}

.alex-preview__questions {
  max-height: 220px;
  overflow-y: auto;
}

.alex-preview__question {
  border-top: 1px solid #eef2f7;
  padding: 8px 0;
}

.alex-preview__question:first-child {
  border-top: none;
  padding-top: 0;
}

.alex-preview__qtitle {
  font-size: 13px;
  color: #1e293b;
}

.alex-preview__qmeta {
  margin-top: 2px;
  font-size: 11px;
  color: #64748b;
  text-transform: uppercase;
}

.alex-preview__required {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  font-size: 10px;
  font-weight: 600;
  color: #b91c1c;
  background: #fee2e2;
  border-radius: 4px;
  text-transform: uppercase;
  vertical-align: middle;
}

.alex-preview__optional {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  font-size: 10px;
  font-weight: 500;
  color: #64748b;
  background: #f1f5f9;
  border-radius: 4px;
}

.alex-preview__skiplogic {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #e2e8f0;
}

.alex-preview__skiplogic-title {
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 6px;
}

.alex-preview__skiplogic-rule {
  font-size: 12px;
  color: #475569;
  padding: 5px 8px;
  margin-bottom: 4px;
  background: #f8fafc;
  border-radius: 6px;
  border-left: 3px solid #6366f1;
  line-height: 1.5;
}

.alex-preview__sl-label {
  font-weight: 600;
  color: #6366f1;
  text-transform: uppercase;
  font-size: 10px;
  letter-spacing: 0.03em;
}

.alex-preview__sl-parent {
  font-weight: 500;
  color: #64748b;
  text-transform: none;
  font-size: 11px;
  letter-spacing: normal;
}

.alex-json-details {
  margin-bottom: 10px;
}

.alex-json-toggle {
  border: 0;
  background: transparent;
  cursor: pointer;
  font-size: 12px;
  color: #4a90d9;
  padding: 0;
}

.alex-json-editor {
  width: 100%;
  box-sizing: border-box;
  font-family: ui-monospace, Menlo, Consolas, monospace;
  font-size: 12px;
  line-height: 1.4;
  padding: 10px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  resize: vertical;
  min-height: 120px;
  margin-bottom: 10px;
}

.alex-chat__composer {
  flex-shrink: 0;
  padding: 10px 12px 12px;
  background: #fff;
  border-top: 1px solid #e2e8f0;
}

.alex-chat__composer--disabled {
  opacity: 0.8;
}

.alex-file {
  display: inline-block;
  margin-bottom: 8px;
  cursor: pointer;
}

.alex-file__input {
  position: absolute;
  width: 0.1px;
  height: 0.1px;
  opacity: 0;
  overflow: hidden;
  z-index: -1;
}

.alex-file__label {
  font-size: 12px;
  color: var(--buttonLinkColor);
  text-decoration: underline;
  cursor: pointer;
}

.alex-attached-file {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  padding: 4px 10px;
  font-size: 12px;
  color: #1e293b;
  background: #f0f9ff;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
}

.alex-attached-file__name {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.alex-attached-file__remove {
  border: none;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  padding: 0;
  font-size: 12px;
  line-height: 1;
}

.alex-attached-file__remove:hover {
  color: #b91c1c;
}

.alex-composer-input {
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 8px;
  padding: 10px 12px;
  font-size: 14px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  resize: none;
}

.alex-composer-input:disabled {
  background: #f8fafc;
  color: #64748b;
  cursor: not-allowed;
}

.alex-btn {
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 14px;
  cursor: pointer;
}

.alex-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.alex-btn--primary {
  background: var(--buttonLinkColor);
  color: var(--buttonTextColor);
}

.alex-btn--send {
  width: 100%;
  background: var(--buttonLinkColor);
  color: var(--buttonTextColor);
}

.alex-btn__loader {
  display: inline-block;
  width: 12px;
  height: 12px;
  margin-right: 8px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-top-color: #fff;
  animation: alex-spin 0.7s linear infinite;
  vertical-align: -1px;
}

.alex-btn--apply {
  background: var(--buttonLinkColor);
  color: var(--buttonTextColor);
}

.alex-btn--apply:hover:not(:disabled) {
  filter: brightness(0.92);
}

.alex-apply-hint {
  margin: 8px 0 0;
  font-size: 12px;
  line-height: 1.4;
  color: #64748b;
}

@keyframes alex-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>



// WEBPACK FOOTER //
// src/components/AIFormBuilder/ChatPanel.vue